<template>
    <b-container fluid class="grid-shell" :style="gridStyle" :data-grid-uid="gridUid" data-test="grid-shell" :data-state="loading ? 'loading' : 'idle'" :aria-busy="loading">
        <component :is="'style'" v-if="hiddenColumnsCss">{{ hiddenColumnsCss }}</component>
        <div ref="scrollContainer" class="grid-shell-scroll" :style="scrollStyle">
            <div class="grid-shell-content">
                <!--Sticky header-->
                <b-card class="grid-shell-header">
                    <div v-if="showToolbar" class="mobile-filter-toggle-bar d-flex d-lg-none align-items-center justify-content-between">
                        <b-button
                            size="sm"
                            :variant="mobileFiltersExpanded || hasActiveFilters ? 'primary' : 'outline-secondary'"
                            class="py-1 px-2 font-weight-bold d-inline-flex align-items-center"
                            :aria-expanded="mobileFiltersExpanded ? 'true' : 'false'"
                            data-test="btn-toggle-mobile-filters"
                            @click="mobileFiltersExpanded = !mobileFiltersExpanded"
                        >
                            <i class="bi bi-sliders mr-1"></i>
                            <span>{{ mobileFiltersExpanded ? 'Hide Filters & View' : 'Filters & View' }}</span>
                            <b-badge v-if="hasActiveFilters" variant="light" class="ml-1">Active</b-badge>
                            <i class="bi ml-1" :class="mobileFiltersExpanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                        </b-button>
                        <b-button
                            v-if="canReset && hasActiveFilters"
                            size="sm"
                            variant="outline-secondary"
                            class="py-1 px-2"
                            @click="$emit('reset-filters')"
                        >
                            <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset
                        </b-button>
                    </div>
                    <div v-if="showToolbar" class="grid-filter-toolbar d-flex align-items-center flex-wrap pb-2 mb-2 border-bottom" :class="{ 'mobile-filters-collapsed': !mobileFiltersExpanded }">
                        <slot name="filters"></slot>
                        <div class="filter-toolbar-group filter-toolbar-view d-flex align-items-center flex-wrap w-100" :class="{ 'filter-toolbar-view-single': !$slots.filters }">
                            <div class="filter-toolbar-label mr-2">
                                <i class="bi bi-eye mr-1"></i><strong>View:</strong>
                            </div>
                            <slot name="view"></slot>
                            <div class="filter-toolbar-view-actions ml-auto d-flex align-items-center">
                                <slot name="view-actions"></slot>
                                <b-button
                                    size="sm"
                                    variant="outline-secondary"
                                    class="d-inline-flex align-items-center mr-1"
                                    :data-test="bestFitTestId"
                                    aria-label="Auto-arrange columns to best fit"
                                    title="Auto-arrange columns to best fit"
                                    v-b-tooltip.hover.top="'Auto-arrange columns to best fit'"
                                    @click="onBestFitClick"
                                >
                                    <i class="bi bi-distribute-horizontal"></i>
                                </b-button>
                                <b-button
                                    size="sm"
                                    variant="outline-secondary"
                                    class="d-inline-flex align-items-center"
                                    :data-test="columnsButtonTestId"
                                    aria-label="Customize visible columns and grid setup"
                                    title="Customize visible columns and grid setup"
                                    v-b-tooltip.hover.top="'Customize visible columns and grid setup'"
                                    @click="onColumnsButtonClick"
                                >
                                    <i class="bi bi-gear"></i>
                                </b-button>
                            </div>
                        </div>
                    </div>
                    <b-row class="my-1 grid-row">
                        <b-col v-for="(col, colIdx) in columns" :key="col.key"
                                :class="['px-1', 'grid-col-' + col.key, col.headerClass || 'text-center', { 'resizable-header-col': resizableColumns && col.resizable !== false && col.key !== 'collapser' }]"
                                :data-col-ref="col.referenceName || col.key">
                            <slot :name="'header-' + col.key">{{ col.label }}</slot>
                            <span v-if="resizableColumns && col.resizable !== false && col.key !== 'collapser' && colIdx < columns.length - 1"
                                  class="col-resize-handle"
                                  :class="{ 'is-resizing': resizingColKey === (col.referenceName || col.key) }"
                                  data-test="col-resize-handle"
                                  :data-col-ref="col.referenceName || col.key"
                                  title="Drag to resize column (double-click to reset)"
                                  @mousedown.stop.prevent="onStartResizeColumn($event, col, colIdx)"
                                  @dblclick.stop.prevent="onResetColumnWidth(col)">
                            </span>
                        </b-col>
                    </b-row>
                </b-card>

                <!--Body (groups / rows)-->
                <slot></slot>

                <!--Empty State-->
                <slot name="empty" v-if="!loading && empty">
                    <grid-empty-state
                        :title="emptyTitle"
                        :message="emptyMessage"
                        :icon="emptyIcon"
                        :has-active-filters="hasActiveFilters"
                        :suggestions="emptySuggestions"
                        :show-reset="canReset && hasActiveFilters"
                        :reset-label="resetLabel"
                        :actions="emptyActions"
                        @reset="$emit('reset-filters')"
                        @action="$emit('empty-action', $event)"
                    />
                </slot>
            </div>
        </div>
        <modal-task-columns
            v-if="!customColumnsHandler"
            ref="builtInModalColumns"
            :modal-id="'modal-grid-columns-' + gridUid"
            :modal-title="setupModalTitle"
            subtitle="Use the eye icon on column badges below to show or hide columns in this grid. Core action columns remain fixed at the end."
            :allow-custom-fields="false"
            skip-remote-save
            :fixed-action-ref="builtInFixedActionRef"
            :default-columns="builtInDefaultColumns"
            :current-columns="builtInCurrentColumns"
            :feature-config="featureConfig"
            @columns-saved="onBuiltInColumnsSaved"
            @feature-settings-saved="$emit('feature-settings-saved', $event)"
        />
    </b-container>
</template>

<script>
    import GridEmptyState from './grid-empty-state.vue';
    import ModalTaskColumns from './modal-task-columns.vue';

    export default {
        name: 'grid-shell',
        components: {
            'grid-empty-state': GridEmptyState,
            'modal-task-columns': ModalTaskColumns
        },
        props: {
            columns: {
                type: Array,
                required: true
            },
            resizableColumns: {
                type: Boolean,
                default: true
            },
            minHeight: {
                type: String,
                default: 'auto'
            },
            maxHeight: {
                type: String,
                default: null
            },
            autoFitHeight: {
                type: Boolean,
                default: false
            },
            bottomOffset: {
                type: Number,
                default: 20
            },
            loading: {
                type: Boolean,
                default: false
            },
            empty: {
                type: Boolean,
                default: false
            },
            emptyTitle: {
                type: String,
                default: ''
            },
            emptyMessage: {
                type: String,
                default: ''
            },
            emptyIcon: {
                type: String,
                default: ''
            },
            hasActiveFilters: {
                type: Boolean,
                default: false
            },
            emptySuggestions: {
                type: Array,
                default: () => []
            },
            canReset: {
                type: Boolean,
                default: true
            },

            resetLabel: {
                type: String,
                default: 'Reset to Defaults'
            },
            showToolbar: {
                type: Boolean,
                default: true
            },
            customColumnsHandler: {
                type: Function,
                default: null
            },
            bestFitTestId: {
                type: String,
                default: 'btn-best-fit-columns'
            },
            columnsButtonTestId: {
                type: String,
                default: 'btn-customize-columns'
            },
            setupModalTitle: {
                type: String,
                default: 'Customize Grid Columns & Setup'
            },
            featureConfig: {
                type: Object,
                default: null
            },
            emptyActions: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                dynamicMaxHeight: null,
                mobileFiltersExpanded: false,
                resizingColKey: null,
                localColumnWidths: {},
                hiddenColumns: {},
                gridUid: 'vt-grid-' + Math.random().toString(36).slice(2, 9)
            };
        },
        computed: {
            gridStyle() {
                const visibleCols = (this.columns || []).filter(c => !this.isColumnHidden(c));
                const template = visibleCols.map(c => { const k = c.referenceName || c.key; const lw = k && this.localColumnWidths[k]; return lw ? `minmax(${lw}, ${lw})` : c.width; }).join(' ');
                return { '--grid-cols': template };
            },
            toggleableColumns() {
                const lockedSet = new Set(['title', 'name', 'item', 'member', 'worktype', 'action', 'actions', 'buttons', 'system.title', 'system.action']);
                return (this.columns || [])
                    .filter(c => c && c.key !== 'collapser' && (c.label || c.key))
                    .map(c => {
                        const colKey = c.referenceName || c.key;
                        const lowerKey = String(colKey || '').toLowerCase();
                        return {
                            raw: c,
                            colKey,
                            displayLabel: c.label || c.key,
                            locked: c.locked === true || lockedSet.has(lowerKey)
                        };
                    });
            },
            visibleToggleableCount() {
                return this.toggleableColumns.filter(tc => !this.isColumnHidden(tc.raw)).length;
            },
            builtInDefaultColumns() {
                return (this.columns || [])
                    .filter(c => c && c.key !== 'collapser' && (c.label || c.key))
                    .map((c, idx) => ({
                        key: c.key || c.referenceName,
                        referenceName: c.referenceName || c.key,
                        label: c.label || c.key,
                        width: c.width || '',
                        headerClass: c.headerClass || '',
                        isSystem: true,
                        order: idx + 1,
                        isVisible: true,
                        hidden: false
                    }));
            },
            builtInCurrentColumns() {
                return this.builtInDefaultColumns.map(c => ({
                    ...c,
                    isVisible: !this.hiddenColumns[c.referenceName],
                    hidden: !!this.hiddenColumns[c.referenceName]
                }));
            },
            builtInFixedActionRef() {
                const cols = this.builtInDefaultColumns;
                if (!cols.length) return 'buttons';
                const last = cols[cols.length - 1];
                return last.referenceName || last.key || 'buttons';
            },
            hiddenColumnsCss() {
                const rules = [];
                (this.columns || []).forEach((col, idx) => {
                    if (this.isColumnHidden(col)) {
                        const nth = idx + 1;
                        rules.push(`.grid-shell[data-grid-uid="${this.gridUid}"] .grid-row:not(.grid-project-row) > [class*="col"]:nth-child(${nth}) { display: none !important; }`);
                    }
                });
                return rules.join('\n');
            },
            scrollStyle() {
                const style = {};
                if (this.minHeight && this.minHeight !== 'auto') {
                    style.minHeight = this.minHeight;
                }
                if (this.autoFitHeight && this.dynamicMaxHeight) {
                    style.maxHeight = this.dynamicMaxHeight;
                } else if (this.maxHeight) {
                    style.maxHeight = this.maxHeight;
                }
                return style;
            }
        },
        watch: {
            autoFitHeight(val) {
                if (val) {
                    this.initAutoFit();
                } else {
                    this.cleanupAutoFit();
            this.cleanupColResize();
                    this.dynamicMaxHeight = null;
                }
            },
            loading(val) {
                if (!val && this.autoFitHeight) {
                    this.$nextTick(() => {
                        this.computeAvailableHeight();
                    });
                }
            }
        },
        mounted() {
            if (this.autoFitHeight) {
                this.initAutoFit();
            }
        },
        beforeUnmount() {
            this.cleanupAutoFit();
            this.cleanupColResize();
        },
        methods: {
            initAutoFit() {
                this.onResizeThrottled = () => {
                    if (this.resizeRaf) {
                        cancelAnimationFrame(this.resizeRaf);
                    }
                    this.resizeRaf = requestAnimationFrame(() => {
                        this.computeAvailableHeight();
                    });
                };
                window.addEventListener('resize', this.onResizeThrottled, { passive: true });
                window.addEventListener('orientationchange', this.onResizeThrottled, { passive: true });

                if (typeof ResizeObserver !== 'undefined') {
                    this.resizeObserver = new ResizeObserver(() => {
                        this.onResizeThrottled();
                    });
                    const parent = this.$el ? this.$el.parentElement : null;
                    if (parent) {
                        this.resizeObserver.observe(parent);
                    }
                }

                this.$nextTick(() => {
                    this.computeAvailableHeight();
                });
            },
            cleanupAutoFit() {
                if (this.onResizeThrottled) {
                    window.removeEventListener('resize', this.onResizeThrottled);
                    window.removeEventListener('orientationchange', this.onResizeThrottled);
                    this.onResizeThrottled = null;
                }
                if (this.resizeObserver) {
                    this.resizeObserver.disconnect();
                    this.resizeObserver = null;
                }
                if (this.resizeRaf) {
                    cancelAnimationFrame(this.resizeRaf);
                    this.resizeRaf = null;
                }
            },
            computeAvailableHeight() {
                if (!this.autoFitHeight) return;
                const el = this.$refs.scrollContainer;
                if (!el) return;
                const rect = el.getBoundingClientRect();
                if (rect.height === 0 && rect.top === 0) return;

                const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                const available = viewportHeight - rect.top - this.bottomOffset;
                const min = (this.minHeight && this.minHeight !== 'auto') ? parseInt(this.minHeight, 10) : 200;
                this.dynamicMaxHeight = `${Math.max(min, Math.floor(available))}px`;
            },
            getUiZoomScale() {
                if (typeof document !== 'undefined' && document.documentElement) {
                    const z = parseFloat(document.documentElement.style.zoom);
                    if (Number.isFinite(z) && z > 0) return z;
                }
                return 1;
            },
            getElementCssWidthPx(el) {
                if (!el) return 0;
                if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
                    const computed = parseFloat(window.getComputedStyle(el).width);
                    if (Number.isFinite(computed) && computed > 0) {
                        return Math.round(computed);
                    }
                }
                if (el.offsetWidth > 0) {
                    return Math.round(el.offsetWidth);
                }
                return Math.round(el.getBoundingClientRect().width / this.getUiZoomScale());
            },
            onStartResizeColumn(event, col) {
                if (!col) return;
                const handleEl = event.currentTarget;
                const parentEl = handleEl ? handleEl.parentElement : null;
                const headerColEl = parentEl && typeof parentEl.closest === 'function'
                    ? (parentEl.closest('.resizable-header-col, .col') || parentEl)
                    : parentEl;
                if (!headerColEl) return;

                this.cleanupColResize();
                const colRef = col.referenceName || col.key;
                const startX = event.clientX;
                const startWidth = this.getElementCssWidthPx(headerColEl);
                const zoomScale = this.getUiZoomScale();
                const minWidth = (colRef === 'System.Title' || col.key === 'title') ? 140 : 60;
                const locks = [];
                if (headerColEl.parentElement) {
                    const headerCells = Array.from(headerColEl.parentElement.children || []);
                    const cols = this.columns || [];
                    for (let i = 0; i < cols.length; i++) {
                        const c = cols[i];
                        if (!c || this.isColumnHidden(c)) continue;
                        const cKey = c.referenceName || c.key;
                        if (!cKey || cKey === colRef) continue;
                        const effectiveTrack = (cKey && this.localColumnWidths[cKey]) || c.width || '';
                        if (/\b\d+(\.\d+)?fr\b/.test(effectiveTrack)) {
                            const cellEl = headerCells.find(el => el.getAttribute && el.getAttribute('data-col-ref') === cKey) || headerCells[i];
                            if (cellEl) {
                                const cellWidthPx = this.getElementCssWidthPx(cellEl);
                                if (cellWidthPx >= 60) {
                                    locks.push({ column: c, key: cKey, widthPx: cellWidthPx });
                                }
                            }
                        }
                    }
                }
                const applyLocksOnce = () => {
                    if (!locks.length) return;
                    const toApply = locks.splice(0, locks.length);
                    for (const lk of toApply) {
                        this.$set(this.localColumnWidths, lk.key, `${lk.widthPx}px`);
                        this.$emit('column-resize', {
                            column: lk.column,
                            referenceName: lk.key,
                            width: `${lk.widthPx}px`,
                            widthPx: lk.widthPx,
                            lockIfUnset: true
                        });
                    }
                };
                let lastAppliedWidth = startWidth;
                let pendingWidth = null;
                let lastWidth = null;

                this.resizingColKey = colRef;
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';

                this._onColResizeMove = (moveEvent) => {
                    const deltaX = (moveEvent.clientX - startX) / zoomScale;
                    const newWidth = Math.max(minWidth, Math.round(startWidth + deltaX));
                    if (newWidth === lastAppliedWidth) return;
                    pendingWidth = newWidth;
                    if (this._colResizeRaf) return;
                    this._colResizeRaf = requestAnimationFrame(() => {
                        this._colResizeRaf = null;
                        if (pendingWidth === null || pendingWidth === lastAppliedWidth) return;
                        applyLocksOnce();
                        lastAppliedWidth = pendingWidth;
                        lastWidth = `${pendingWidth}px`;
                        if (colRef) {
                            this.$set(this.localColumnWidths, colRef, lastWidth);
                        }
                        this.$emit('column-resize', { column: col, referenceName: colRef, width: lastWidth, widthPx: pendingWidth });
                    });
                };
                this._onColResizeUp = () => {
                    if (this._colResizeRaf) {
                        cancelAnimationFrame(this._colResizeRaf);
                        this._colResizeRaf = null;
                    }
                    if (pendingWidth !== null && pendingWidth !== lastAppliedWidth) {
                        applyLocksOnce();
                        lastAppliedWidth = pendingWidth;
                        lastWidth = `${pendingWidth}px`;
                        if (colRef) {
                            this.$set(this.localColumnWidths, colRef, lastWidth);
                        }
                        this.$emit('column-resize', { column: col, referenceName: colRef, width: lastWidth, widthPx: pendingWidth });
                    }
                    const finalWidth = lastWidth;
                    this.cleanupColResize();
                    if (finalWidth) {
                        this.$emit('column-resize-end', { column: col, referenceName: colRef, width: finalWidth });
                    }
                };
                window.addEventListener('mousemove', this._onColResizeMove);
                window.addEventListener('mouseup', this._onColResizeUp);
            },
            onResetColumnWidth(col) {
                if (!col) return;
                const colRef = col.referenceName || col.key;
                if (colRef && this.localColumnWidths[colRef]) {
                    this.$delete(this.localColumnWidths, colRef);
                }
                this.$emit('column-resize-reset', { column: col, referenceName: colRef });
            },
            resetAllColumnWidths() {
                this.localColumnWidths = {};
            },
            isColumnHidden(col) {
                if (!col) return false;
                const colKey = col.referenceName || col.key;
                return !!(colKey && this.hiddenColumns[colKey]);
            },
            toggleBuiltInColumn(colKey, isVisible) {
                if (!colKey) return;
                if (isVisible) {
                    this.$delete(this.hiddenColumns, colKey);
                } else {
                    this.$set(this.hiddenColumns, colKey, true);
                }
            },
            onResetBuiltInColumns() {
                this.hiddenColumns = {};
                this.resetAllColumnWidths();
            },
            onColumnsButtonClick() {
                if (typeof this.customColumnsHandler === 'function') {
                    this.customColumnsHandler();
                    return;
                }
                if (this.$refs.builtInModalColumns && typeof this.$refs.builtInModalColumns.showModal === 'function') {
                    this.$refs.builtInModalColumns.showModal();
                }
            },
            onBuiltInColumnsSaved(savedCols) {
                const nextHidden = {};
                (savedCols || []).forEach(c => {
                    const ref = c.referenceName || c.key;
                    if (ref && (c.hidden || c.isVisible === false)) {
                        nextHidden[ref] = true;
                    }
                });
                this.hiddenColumns = nextHidden;
                this.$emit('columns-saved', savedCols);
            },
            onBestFitClick() {
                this.resetAllColumnWidths();
                this.$emit('best-fit');
            },
            cleanupColResize() {
                if (this._colResizeRaf) {
                    cancelAnimationFrame(this._colResizeRaf);
                    this._colResizeRaf = null;
                }
                if (this._onColResizeMove) {
                    window.removeEventListener('mousemove', this._onColResizeMove);
                    this._onColResizeMove = null;
                }
                if (this._onColResizeUp) {
                    window.removeEventListener('mouseup', this._onColResizeUp);
                    this._onColResizeUp = null;
                }
                if (this.resizingColKey) {
                    this.resizingColKey = null;
                    if (typeof document !== 'undefined' && document.body) {
                        document.body.style.cursor = '';
                        document.body.style.userSelect = '';
                    }
                }
            }
        }
    };
</script>

<style scoped>
    .grid-shell-scroll {
        overflow-x: auto;
        overflow-y: auto;
        width: 100%;
        max-height: calc(100vh - 180px);
        scrollbar-width: thin;
        scrollbar-color: var(--vt-scrollbar-thumb, #475569) var(--vt-scrollbar-track, transparent);
    }
    .grid-shell-scroll::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }
    .grid-shell-scroll::-webkit-scrollbar-track {
        background: var(--vt-scrollbar-track, transparent);
        border-radius: 4px;
    }
    .grid-shell-scroll::-webkit-scrollbar-thumb {
        background: var(--vt-scrollbar-thumb, #475569);
        border-radius: 4px;
    }
    .grid-shell-scroll::-webkit-scrollbar-thumb:hover {
        background: var(--vt-scrollbar-thumb-hover, #64748b);
    }
    .grid-shell-content {
        min-width: 100%;
        width: -moz-fit-content;
        width: fit-content;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
    .grid-shell-header {
        position: sticky;
        top: 0;
        z-index: 20;
    }

    @media (max-width: 991.98px) {
        .grid-shell-scroll {
            max-height: none !important;
            overflow-x: visible !important;
            overflow-y: visible !important;
        }
        .grid-shell-content {
            width: 100% !important;
            min-width: 0 !important;
        }
        .grid-shell-header {
            position: static;
            margin-bottom: 0.35rem;
        }
    }
    .grid-shell-header-noop {
    }
</style>

<style>
    /* Shared (non-scoped) grid styling so slotted rows inherit the layout. */
    .grid-shell .card {
        min-width: 100%;
        width: 100%;
        box-sizing: border-box;
    }
    .grid-shell .btn {
        font-size: 0.75rem;
        padding: 0.15rem 0.3rem;
    }
    .grid-shell .btn-primary,
    .mobile-filter-toggle-bar .btn-primary {
        background-color: #007bff !important;
        border-color: #007bff !important;
        color: #ffffff !important;
    }
    .grid-shell .btn-primary:hover,
    .mobile-filter-toggle-bar .btn-primary:hover {
        background-color: #0069d9 !important;
        border-color: #0062cc !important;
        color: #ffffff !important;
    }
    [data-theme="dark"] .grid-shell .btn-primary,
    [data-theme="dark"] .mobile-filter-toggle-bar .btn-primary {
        background-color: #0284c7 !important;
        border-color: #0284c7 !important;
        color: #ffffff !important;
    }
    [data-theme="dark"] .grid-shell .btn-primary:hover,
    [data-theme="dark"] .mobile-filter-toggle-bar .btn-primary:hover {
        background-color: #0369a1 !important;
        border-color: #0369a1 !important;
        color: #ffffff !important;
    }
    .grid-shell .card-body {
        padding: 0.3rem 0.5rem;
    }
    .grid-shell .badge {
        font-weight: 500;
        white-space: normal;
        text-align: left;
    }

    .grid-shell .grid-row {
        display: grid !important;
        width: 100%;
        min-width: 0;
        margin-left: 0;
        margin-right: 0;
        align-items: center;
        column-gap: 0.35rem;
        box-sizing: border-box;
        grid-template-columns: var(--grid-cols);
    }
    .grid-shell .grid-row > [class*="col"] {
        flex: none;
        width: auto;
        max-width: none;
        min-width: 0;
        padding-left: 0;
        padding-right: 0;
    }
    /* Header cells align to the top so single-line labels line up with
       multi-line headers that include a filter/control underneath. */
    .grid-shell-header .grid-row {
        align-items: stretch;
    }
    .grid-shell-header .grid-row > [class*="col"] {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }
    .grid-shell-header .grid-row > [class*="col"].text-left {
        align-items: flex-start;
    }
    .grid-shell-header .grid-row > .resizable-header-col {
        position: relative;
        user-select: none;
    }
    .grid-shell .col-resize-handle {
        position: absolute;
        top: -2px;
        bottom: -2px;
        right: -5px;
        width: 9px;
        cursor: col-resize;
        z-index: 15;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .grid-shell .col-resize-handle::after {
        content: "";
        width: 1px;
        height: 80%;
        min-height: 24px;
        background-color: rgba(148, 163, 184, 0.35);
        border-radius: 1px;
        transition: background-color 0.15s ease, width 0.15s ease, height 0.15s ease, box-shadow 0.15s ease;
    }
    [data-theme="dark"] .grid-shell .col-resize-handle::after {
        background-color: rgba(148, 163, 184, 0.28);
    }
    .grid-shell-header .grid-row > .resizable-header-col:hover > .col-resize-handle::after {
        background-color: rgba(100, 116, 139, 0.65);
    }
    [data-theme="dark"] .grid-shell-header .grid-row > .resizable-header-col:hover > .col-resize-handle::after {
        background-color: rgba(148, 163, 184, 0.55);
    }
    .grid-shell .col-resize-handle:hover::after,
    .grid-shell .col-resize-handle.is-resizing::after {
        width: 3px !important;
        height: 100% !important;
        background-color: #0284c7 !important;
        box-shadow: 0 0 6px rgba(2, 132, 199, 0.45);
    }
    [data-theme="dark"] .grid-shell .col-resize-handle:hover::after,
    [data-theme="dark"] .grid-shell .col-resize-handle.is-resizing::after {
        background-color: #38bdf8 !important;
        box-shadow: 0 0 8px rgba(56, 189, 248, 0.55);
    }
    /* Group header rows span freely instead of following the grid template. */
    .grid-shell .grid-row.grid-project-row {
        display: flex !important;
        align-items: center;
    }

    /* Row hover highlight to correlate left-most columns with right-most actions */
    .grid-shell .grid-row:not(.grid-project-row) {
        transition: background-color 0.12s ease, box-shadow 0.12s ease;
    }
    .grid-shell :not(.grid-shell-header) > .grid-row:not(.grid-project-row):hover,
    .grid-shell .grid-row.repo-branch-row:hover {
        background-color: rgba(2, 132, 199, 0.08) !important;
        box-shadow: inset 3px 0 0 #0284c7;
    }
    [data-theme="dark"] .grid-shell :not(.grid-shell-header) > .grid-row:not(.grid-project-row):hover,
    [data-theme="dark"] .grid-shell .grid-row.repo-branch-row:hover {
        background-color: rgba(56, 189, 248, 0.11) !important;
        box-shadow: inset 3px 0 0 #38bdf8;
    }

    /* Shared Grid Filter Toolbar */
    .grid-filter-toolbar {
        gap: 0.4rem;
        padding: 0.2rem 0.25rem;
    }
    .grid-filter-toolbar .filter-toolbar-label {
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--vt-muted, #6c757d);
        display: flex;
        align-items: center;
        white-space: nowrap;
    }
    .grid-filter-toolbar .filter-toolbar-view {
        gap: 0.4rem;
        flex-basis: 100%;
        width: 100%;
        margin-top: 0.25rem;
        padding-top: 0.25rem;
        border-top: 1px dashed rgba(148, 163, 184, 0.25);
    }
    .grid-filter-toolbar .filter-toolbar-view.filter-toolbar-view-single {
        margin-top: 0;
        padding-top: 0;
        border-top: none;
    }
    .grid-filter-toolbar .filter-toolbar-view-actions {
        gap: 0.4rem;
        margin-left: auto;
        display: flex;
        align-items: center;
        flex-shrink: 0;
    }
    .grid-filter-toolbar .filter-toolbar-actions {
        gap: 0.4rem;
        margin-left: auto;
        display: flex;
        align-items: center;
        flex-shrink: 0;
    }
    .grid-filter-chip {
        position: relative;
    }
    .grid-filter-chip.show,
    .grid-filter-chip.b-dropdown.show {
        z-index: 1050 !important;
    }
    .grid-filter-chip .dropdown-menu {
        z-index: 1060 !important;
    }
    .grid-filter-chip .btn,
    .btn-filter-pill {
        border-radius: 20px !important;
        font-size: 0.78rem !important;
        padding: 0.2rem 0.65rem !important;
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        font-weight: 500;
        line-height: 1.3;
    }
    .grid-filter-chip .filter-chevron {
        font-size: 0.65rem;
        opacity: 0.7;
    }
    .filter-chip-dropdown-form {
        min-width: 210px;
        max-height: 320px;
        overflow-y: auto;
    }
    .column-filter-icon {
        font-size: 0.75rem;
        color: #007bff;
        margin-left: 0.25rem;
    }
    [data-theme="dark"] .column-filter-icon {
        color: #38bdf8;
    }
    [data-theme="dark"] .grid-filter-chip .btn-outline-secondary {
        background-color: #222d3d !important;
        border-color: #3e4c5e !important;
        color: #e2e8f0 !important;
    }
    [data-theme="dark"] .grid-filter-chip .btn-outline-secondary:hover {
        background-color: #2d3b4e !important;
        border-color: #4b5d73 !important;
        color: #ffffff !important;
    }

    /* Shared Row Expand Chevron Affordance */
    .row-expand-btn {
        border: none !important;
        background: transparent !important;
        padding: 0.1rem 0.2rem !important;
        line-height: 1;
        box-shadow: none !important;
        color: var(--vt-muted, #6c757d);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: color 0.15s ease, background-color 0.15s ease;
        border-radius: 4px;
        min-width: 18px;
        min-height: 18px;
        width: 18px;
        height: 18px;
    }
    .row-expand-btn:hover {
        color: var(--vt-text, #212529) !important;
        background-color: rgba(0, 0, 0, 0.06) !important;
    }
    [data-theme="dark"] .row-expand-btn:hover {
        color: #ffffff !important;
        background-color: rgba(255, 255, 255, 0.1) !important;
    }
    .row-expand-icon {
        display: inline-block;
        transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        font-size: 0.78rem;
    }
    .row-expand-icon.is-expanded {
        transform: rotate(90deg);
    }
    @media (max-width: 991.98px) {
        .grid-shell {
            padding-left: 0.25rem !important;
            padding-right: 0.25rem !important;
        }
        .grid-shell .grid-shell-header .grid-row {
            display: none !important;
        }
        .grid-shell .grid-row {
            display: flex !important;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.45rem 0.65rem;
            padding: 0.35rem 0.25rem;
        }
        .grid-shell .grid-row > [class*="col"] {
            flex: 0 1 auto;
            width: auto;
            max-width: 100%;
        }
        .grid-shell .grid-row > [class*="title"],
        .grid-shell .grid-row > .grid-col-title,
        .grid-shell .grid-row > .pr-col-title {
            order: 2 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            margin-top: 0.2rem !important;
            margin-bottom: 0.35rem !important;
            text-align: left !important;
        }
        .grid-shell .grid-row > [class*="title"] textarea,
        .grid-shell .grid-row > .grid-col-title textarea,
        .grid-shell .grid-row > .pr-col-title textarea {
            font-size: 0.95rem !important;
            font-weight: 600 !important;
            line-height: 1.35 !important;
        }
        .grid-shell .grid-row > [class*="buttons"],
        .grid-shell .grid-row > [class*="actions"] {
            margin-left: auto !important;
        }
        .grid-filter-toolbar.mobile-filters-collapsed {
            display: none !important;
        }
        .mobile-filter-toggle-bar + .grid-filter-toolbar:not(.mobile-filters-collapsed) {
            margin-top: 0.5rem !important;
        }
        .grid-shell-header:not(:has(.grid-filter-toolbar)) {
            display: none !important;
        }
        .grid-filter-toolbar {
            border-bottom: none !important;
            margin-bottom: 0 !important;
            padding: 0.65rem 0.75rem !important;
            background-color: var(--vt-surface-subtle, rgba(248, 250, 252, 0.8)) !important;
            border: 1px solid var(--vt-border, #e2e8f0) !important;
            border-radius: 0.65rem !important;
            gap: 0.45rem !important;
            display: flex !important;
            flex-wrap: wrap !important;
            align-items: center !important;
        }
        [data-theme="dark"] .grid-filter-toolbar {
            background-color: rgba(15, 23, 42, 0.5) !important;
            border-color: rgba(255, 255, 255, 0.08) !important;
        }
        .grid-filter-toolbar > .filter-toolbar-label {
            width: 100% !important;
            flex: 0 0 100% !important;
            font-size: 0.76rem !important;
            margin-bottom: 0.15rem !important;
            color: var(--vt-muted, #94a3b8) !important;
            text-transform: uppercase !important;
            letter-spacing: 0.04em !important;
        }
        .grid-filter-toolbar .input-group {
            width: 100% !important;
            max-width: 100% !important;
            flex: 1 1 100% !important;
            margin-right: 0 !important;
        }
        .grid-filter-toolbar .grid-filter-chip,
        .grid-filter-toolbar .dropdown {
            flex: 0 1 auto !important;
            margin-right: 0.3rem !important;
            margin-bottom: 0.2rem !important;
        }
        .grid-filter-toolbar .filter-toolbar-actions {
            flex: 0 0 100% !important;
            width: 100% !important;
            margin-left: 0 !important;
            margin-top: 0.35rem !important;
            padding-top: 0.45rem !important;
            border-top: 1px dashed var(--vt-border, rgba(148, 163, 184, 0.25)) !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            gap: 0.4rem !important;
        }
        .grid-filter-toolbar .filter-toolbar-actions .btn {
            border-radius: 0.45rem !important;
            padding: 0.28rem 0.75rem !important;
            font-size: 0.8rem !important;
        }
        .grid-filter-toolbar .filter-toolbar-view {
            flex: 0 0 100% !important;
            width: 100% !important;
            display: flex !important;
            flex-wrap: wrap !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 0.45rem !important;
            margin-top: 0.35rem !important;
            padding-top: 0.45rem !important;
            border-top: 1px dashed var(--vt-border, rgba(148, 163, 184, 0.25)) !important;
        }
        .grid-filter-toolbar .filter-toolbar-view > .filter-toolbar-label {
            width: auto !important;
            flex: 0 0 auto !important;
            margin-bottom: 0 !important;
        }
        .grid-filter-toolbar .filter-toolbar-view .btn-outline-primary,
        .grid-filter-toolbar .filter-toolbar-view [data-test="btn-open-ado-branches"] {
            white-space: nowrap !important;
            height: auto !important;
            min-height: 26px !important;
            padding: 0.2rem 0.5rem !important;
            font-size: 0.76rem !important;
            line-height: 1.2 !important;
            display: inline-flex !important;
            align-items: center !important;
        }
    }
    @media (min-width: 576px) and (max-width: 991.98px) {
        .grid-filter-toolbar .input-group {
            flex: 1 1 calc(50% - 0.35rem) !important;
            min-width: 180px !important;
        }
    }
    .row-expand-placeholder {
        display: inline-block;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }
</style>
